import { GenericError, UnknownError } from '@cian/peperrors';

import { trackAddNewUserInChat } from './tracking';
import { IMessageEvent, IWsFileMessage, IWsMessageEvent } from './types';
import { BLOB_ID } from '../../constants/blobId';
import { GuardError } from '../../errors/GuardError';
import { IOfferSchema } from '../../repositories/chats/entities/get_my_offers_for_selection/OfferSchema';
import { currentChatOpponentIdSelector, currentOpenChatSelector } from '../../selectors';
import { setSocketStatusFailed } from '../../slices/socketStatusSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { TMessageSchema } from '../../types/messages';
import { TThunkAction } from '../../types/redux/thunk';
import { TSessionSucceedState } from '../../types/session';
import { ETelemetryEvents } from '../../types/telemetry';
import { timeoutPromise } from '../../utils/timeoutPromise';
import { setSendMessageFailed } from '../sendMessage';

/**
 * Отправка сообщения по WS
 */

interface IProps {
  message: TMessageSchema;
  files?: IWsFileMessage[];
  offers?: IOfferSchema[];
}

const DOMAIN = 'critical.messageSend';
const DEFAULT_MESSAGE_SEND_TIMEOUT = 10000;

export const sendWsMessage =
  ({ message, files, offers }: IProps): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry, config, custom: { messagesMap, socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const state = getState();

    const {
      session: { status, token },
      socketStatus,
    } = state;

    const opponentId = currentChatOpponentIdSelector(state);
    const { users, offer } = currentOpenChatSelector(state) || {};

    // в модалке каждый раз когда возвращемся на экран - перезапрашивается сессия
    const isSessionExpired = status !== ERequestApiStatus.Succeed && status !== ERequestApiStatus.Pending;
    const isSocketIsClose = eventChannel.isChannelClosed() || socketStatus === ERequestApiStatus.Failed;

    const { chatId, messageId, text, userId, payload } = message;

    const isMessageUserInChat = users?.find(user => user.userId === userId);

    const messageEvent: IMessageEvent = {
      chatId,
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.Message,
      timestamp: Date.now(),
    };

    try {
      telemetry.increment(ETelemetryEvents.ChatWsMessageSendInit);

      if (isSocketIsClose) {
        dispatch(setSocketStatusFailed());

        throw new GuardError({
          message: 'Соединение закрыто',
          domain: DOMAIN,
        });
      }

      if (isSessionExpired) {
        throw new GuardError({
          message: 'Сессия истекла',
          domain: DOMAIN,
        });
      }

      if (!chatId || chatId === BLOB_ID) {
        throw new GuardError({
          message: 'Чат не выбран',
          domain: DOMAIN,
        });
      }

      clientEvents.set(messageId, messageEvent);

      const wsMessageEvent: IWsMessageEvent = {
        type: EWsClientEventType.Message,
        message: {
          chatId,
          text,
          files,
          offers,
          payload,
        },
        uid: messageId,
        userId,
        sessionToken: (token as TSessionSucceedState['token']).sessionToken,
      };

      const messageSendTimeout =
        config.get<number>('frontend-chats.messageSendTimeout') || DEFAULT_MESSAGE_SEND_TIMEOUT;

      await timeoutPromise(eventChannel.sendMessage(wsMessageEvent), messageSendTimeout, DOMAIN);

      if (!isMessageUserInChat) {
        trackAddNewUserInChat({ offer, chatId, opponentId });
      }
    } catch (error) {
      telemetry.increment(ETelemetryEvents.ChatWsMessageSendFail);

      let errorToLog = error;

      if (!GenericError.isLike(error)) {
        errorToLog = new UnknownError({
          message: 'Неизвестная ошибка при отправке сообщения',
          domain: DOMAIN,
          details: error,
        });
      }

      logger.error(errorToLog);

      clientEvents.set(messageId, { ...messageEvent, status: ERequestApiStatus.Failed });

      dispatch(setSendMessageFailed({ message, messageId, messagesMap }));
    }
  };
